import React, { useState, useEffect } from 'react';
import http from 'Utils/http';
import { Box, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import { setFlow } from 'Actions/flow';
import { Transition } from 'Shared/Transition';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { process } from 'Utils/errorHandler';

const FlowPostParameters = ({ flowId, visible, setVisible, postParameters, onUpdate }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [queryParams, setQueryParams] = useState('');
    const [jsonBody, setJsonBody] = useState('');
    const [jsonError, setJsonError] = useState('');

    useEffect(() => {
        if (postParameters) {
            setQueryParams(postParameters.query || '');
            setJsonBody(postParameters.body || '');
        } else {
            setQueryParams('');
            setJsonBody('');
        }
    }, [postParameters, visible]);

    const validateJson = (value) => {
        if (!value.trim()) {
            setJsonError('');
            return true;
        }
        try {
            JSON.parse(value);
            setJsonError('');
            return true;
        } catch (e) {
            setJsonError('Invalid JSON: ' + e.message);
            return false;
        }
    };

    const onClose = () => {
        setVisible(false);
        setJsonError('');
    };

    const handleSave = async () => {
        if (!validateJson(jsonBody)) {
            return;
        }

        dispatch(setLoading(true));

        const params = {
            post_parameters: {
                query: queryParams.trim(),
                body: jsonBody.trim(),
            }
        };

        http.post(`/flow/${flowId}/update`, params)
            .then((res) => {
                enqueueSnackbar('Parameters saved', {
                    action: CustomSnackbar,
                    variant: 'success',
                });
                dispatch(setFlow(res.data.flow));
                if (onUpdate) {
                    onUpdate(params.post_parameters);
                }
                onClose();
            })
            .catch((err) => {
                process(err, enqueueSnackbar);
            })
            .finally(() => {
                dispatch(setLoading(false));
            });
    };

    const handleClear = async () => {
        dispatch(setLoading(true));

        http.post(`/flow/${flowId}/update`, { post_parameters: [] })
            .then((res) => {
                enqueueSnackbar('Parameters cleared', {
                    action: CustomSnackbar,
                    variant: 'success',
                });
                dispatch(setFlow(res.data.flow));
                setQueryParams('');
                setJsonBody('');
                if (onUpdate) {
                    onUpdate(null);
                }
                onClose();
            })
            .catch((err) => {
                process(err, enqueueSnackbar);
            })
            .finally(() => {
                dispatch(setLoading(false));
            });
    };

    return (
        <Dialog
            open={visible}
            onClose={onClose}
            TransitionComponent={Transition}
            sx={{
                '& .MuiDialog-paper': {
                    minWidth: '500px',
                    maxWidth: '700px',
                },
            }}
        >
            <DialogTitle
                sx={{
                    paddingBottom: '8px',
                    color: '#3B465C',
                    fontWeight: 600,
                }}
            >
                POST Parameters
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        fullWidth
                        label="Query Parameters"
                        placeholder="timeout=30&debug=true"
                        value={queryParams}
                        onChange={(e) => setQueryParams(e.target.value)}
                        helperText="URL query string format (without leading ?)"
                        size="small"
                    />
                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        label="JSON Body"
                        placeholder='{"key": "value"}'
                        value={jsonBody}
                        onChange={(e) => {
                            setJsonBody(e.target.value);
                            validateJson(e.target.value);
                        }}
                        error={!!jsonError}
                        helperText={jsonError || "JSON object to merge with request body"}
                        size="small"
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
                <Button onClick={handleClear} variant="outlined" color="error">
                    Clear
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={!!jsonError}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FlowPostParameters;
